import { useEffect, useState } from "react";

import { ApiError, apiClient } from "../../api/client";
import type {
  AssignBlockInput,
  Block,
  BlockDetail,
  BlockLevel,
  MeResponse,
  UpdateBlockDetailsInput,
  UpdateProgressInput,
  UpdateScoreInput
} from "../../api/types";
import { hasPermission } from "../../auth/permissions";
import { UserPicker, type UserPickerValue } from "../../components/UserPicker";
import { UserRefChip } from "../../components/UserRefChip";
import { useIdempotencyKey } from "../../lib/idempotencyKey";
import { useMemberRecipients } from "../../lib/useMemberRecipients";
import { useRouter } from "../../router/Router";
import { childrenLabel, levelLabel, OverdueChip, statusLabel } from "./BlockCard";
import { canReopen, canSubmitCompletion } from "./completionOptions";
import { ContributorsPanel } from "./ContributorsPanel";
import { canAddChild, isExecution } from "./createBlockOptions";
import { dueDateLabel } from "./dueDate";
import { pillarTint } from "./pillarColors";
import { calcBlockSize } from "./sizing";

const scores = [1, 2, 3, 4, 5];

interface State {
  status: "loading" | "ready" | "notfound" | "error";
  block: BlockDetail | null;
  errorMessage: string | null;
}

const initial: State = { status: "loading", block: null, errorMessage: null };

// The level a child of this block must be: a top owns mids, a mid owns ics, an
// ic is a leaf (C.8).
function childLevelFor(level: BlockLevel): "mid" | "ic" | null {
  if (level === "top") {
    return "mid";
  }
  if (level === "mid") {
    return "ic";
  }
  return null;
}

export function BlockDetailPage({ me, blockId }: { me: MeResponse; blockId: string }) {
  const router = useRouter();
  const [state, setState] = useState<State>(initial);

  useEffect(() => {
    let cancelled = false;
    setState(initial);
    void (async () => {
      try {
        const block = await apiClient.getBlock(blockId);
        if (!cancelled) {
          setState({ status: "ready", block, errorMessage: null });
        }
      } catch (caught) {
        if (cancelled) {
          return;
        }
        if (caught instanceof ApiError && caught.status === 404) {
          setState({ status: "notfound", block: null, errorMessage: null });
          return;
        }
        setState({ status: "error", block: null, errorMessage: messageFor(caught) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [blockId]);

  async function reload() {
    try {
      const block = await apiClient.getBlock(blockId);
      setState({ status: "ready", block, errorMessage: null });
    } catch (caught) {
      setState({ status: "error", block: null, errorMessage: messageFor(caught) });
    }
  }

  if (state.status === "loading") {
    return (
      <section className="card">
        <p className="loading">Loading…</p>
      </section>
    );
  }
  if (state.status === "notfound") {
    return (
      <section className="card empty-state">
        <h2>Block not found</h2>
        <p className="muted">It may not exist, or you may not have access to it.</p>
        <p>
          <button type="button" className="btn btn--ghost" onClick={() => router.navigate("/blocks")}>
            Back to dashboard
          </button>
        </p>
      </section>
    );
  }
  if (state.status === "error" || state.block === null) {
    return (
      <section className="card">
        <p className="form-row__error">{state.errorMessage ?? "Could not load block."}</p>
      </section>
    );
  }

  const block = state.block;
  const childLevel = childLevelFor(block.level);
  // A closed season's record is frozen and the server rejects every write to
  // it, so the page offers none.
  const writable = block.season_status !== "closed";
  const canCreateSub = writable && canAddChild(block, me);
  // Scoring is top-only; progress is set on sub-blocks (a top's progress rolls
  // up from its children) and only while the block is still in flight —
  // completion goes through Mark complete instead.
  const canScore = writable && block.level === "top" && hasPermission(me, "blocks:update_scores");
  // Mirrors the server's Execution rules (C.8): never a top block's owners or
  // notes, and details/progress only on blocks it owns.
  const executionTop = isExecution(me) && block.level === "top";
  const executionNonOwner = isExecution(me) && block.exec_owner?.id !== me.user.id && block.assignee?.id !== me.user.id;
  // Editing descriptive fields uses the level-matching update permission.
  const canEdit =
    writable &&
    hasPermission(me, block.level === "top" ? "blocks:update_top" : "blocks:update_sub") &&
    !executionNonOwner;
  const canEditSize = block.level !== "top" && canEdit && hasPermission(me, "blocks:update_scores");
  const canNotes = writable && hasPermission(me, "blocks:assign") && !executionTop;
  const canProgress =
    writable &&
    block.level !== "top" &&
    (block.status === "assigned" || block.status === "in-progress") &&
    hasPermission(me, "blocks:update_progress") &&
    !executionNonOwner;
  const canAssign = writable && hasPermission(me, "blocks:assign") && !executionTop;
  const canComplete = writable && canSubmitCompletion(block, me);
  const canReopenBlock = writable && canReopen(block, me);

  return (
    <div className="blocks-view">
      <section
        className="card block-detail block-detail--tinted"
        style={{ "--pillar-tint": pillarTint(block.pillar_id, block.pillar) } as React.CSSProperties}
      >
        <button
          type="button"
          className="block-detail__back"
          onClick={() => router.navigate(block.parent_id !== null ? `/blocks/${block.parent_id}` : "/blocks")}
        >
          ← Back
        </button>
        <h2>{block.name}</h2>
        <div className="block-card__tags">
          <span className="chip">{levelLabel(block.level)}</span>
          {block.pillar !== null && <span className="chip">{block.pillar}</span>}
          {block.impact !== null && block.significance !== null && (
            <span className="chip chip--impact">
              Impact {block.impact} · Sig {block.significance}
            </span>
          )}
          <span className={`chip chip--status status-${block.status}`}>{statusLabel(block.status)}</span>
          <span className="chip">{dueDateLabel(block.due_date)}</span>
          <OverdueChip dueDate={block.due_date} status={block.status} />
          {block.exec_owner !== null && <UserRefChip user={block.exec_owner} title="Owner" />}
          {block.assignee !== null && <UserRefChip user={block.assignee} title="Assignee" />}
          {block.team !== null && <span className="chip">{block.team}</span>}
        </div>

        {!writable && <p className="block-detail__banner">Season closed — read only.</p>}
        {block.status === "pending-approval" && (
          <p className="block-detail__banner block-detail__banner--pending" role="status">
            Submitted for approval — this block's completion is waiting for sign-off.
          </p>
        )}

        {block.acceptance_criteria !== null && (
          <p className="block-detail__ac">
            <strong>Acceptance Criteria:</strong> {block.acceptance_criteria}
          </p>
        )}

        <div className="score-box-row">
          <ScoreBox label="Block Size" value={String(block.size)} />
          <ScoreBox label="Progress" value={`${block.progress}%`} />
          <ScoreBox label={childrenLabel(block.level)} value={String(block.child_count)} />
        </div>

        {block.level === "top" && block.calc_size !== null && (
          <p className="muted">
            Calculated {block.calc_size}
            {block.override_size !== null ? ` · Override ${block.override_size}` : " · no override applied"}
          </p>
        )}

        {/* Ownership needn't reach 100% — a project may be only partly covered
            by owned children, so surface the coverage rather than enforcing it.
            Over-allocation is legal too, but flag it so it reads as deliberate. */}
        {block.child_count > 0 && block.size > 0 && (
          <p className={block.child_size_sum > block.size ? "coverage-warning" : "muted"}>
            Children cover {block.child_size_sum} of {block.size} blocks (
            {Math.round((block.child_size_sum / block.size) * 100)}%)
          </p>
        )}

        <div className="block-progress">
          <div
            className={`block-progress__fill${block.status === "complete" ? " is-complete" : ""}`}
            style={{ width: `${Math.min(block.progress, 100)}%` }}
          />
        </div>

        {block.notes !== null && <p className="block-detail__notes">{block.notes}</p>}
      </section>

      {(canScore || canProgress || canAssign || canEdit || canNotes || canComplete || canReopenBlock) && (
        <section className="card">
          <h3>Update</h3>
          {(canComplete || canReopenBlock) && (
            <CompletionPanel
              block={block}
              canComplete={canComplete}
              canReopen={canReopenBlock}
              onSaved={() => void reload()}
            />
          )}
          {(canEdit || canNotes) && (
            <EditPanel
              block={block}
              canEditDetails={canEdit}
              canEditSize={canEditSize}
              canNotes={canNotes}
              onSaved={() => void reload()}
            />
          )}
          {canScore && <ScorePanel block={block} onSaved={() => void reload()} />}
          {canProgress && <ProgressPanel block={block} onSaved={() => void reload()} />}
          {canAssign && <AssignPanel block={block} onSaved={() => void reload()} />}
        </section>
      )}

      {childLevel !== null && (
        <section className="card">
          <h3>{childrenLabel(block.level)}</h3>
          {block.children.length === 0 ? (
            <p className="muted">No {childrenLabel(block.level).toLowerCase()} yet.</p>
          ) : (
            <div className="subblock-tree">
              {block.children.map((child) => (
                <SubBlockRow key={child.id} child={child} onOpen={(id) => router.navigate(`/blocks/${id}`)} />
              ))}
            </div>
          )}

          {canCreateSub && (
            <CreateSubForm parentID={block.id} level={childLevel} onCreated={() => void reload()} />
          )}
        </section>
      )}

      <ContributorsPanel me={me} block={block} onSaved={() => void reload()} />
    </div>
  );
}

function ScoreBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="score-box">
      <div className="score-box__value">{value}</div>
      <div className="score-box__label">{label}</div>
    </div>
  );
}

function SubBlockRow({ child, onOpen }: { child: Block; onOpen: (id: string) => void }) {
  const ownerRef = child.exec_owner ?? child.assignee;
  const owner = ownerRef === null ? "" : ` · ${ownerRef.name.trim() === "" ? ownerRef.email : ownerRef.name}`;
  return (
    <button type="button" className="subblock-row" onClick={() => onOpen(child.id)}>
      <div className="subblock-row__main">
        <span className="subblock-row__name">{child.name}</span>
        <span className="subblock-row__meta">
          {levelLabel(child.level)}
          {owner}
          {child.team !== null ? ` · ${child.team}` : ""}
          {` · ${dueDateLabel(child.due_date)}`}
          {child.child_count > 0 ? ` · ${child.child_count} sub-block${child.child_count > 1 ? "s" : ""}` : ""}
        </span>
      </div>
      <div className="subblock-row__side">
        <OverdueChip dueDate={child.due_date} status={child.status} />
        <span className={`chip chip--status status-${child.status}`}>{statusLabel(child.status)}</span>
        <span className="subblock-row__size">{child.size}</span>
      </div>
    </button>
  );
}

function CreateSubForm({
  parentID,
  level,
  onCreated
}: {
  parentID: string;
  level: "mid" | "ic";
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [size, setSize] = useState(0);
  const [team, setTeam] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { keyFor, reset } = useIdempotencyKey();

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (name.trim() === "" || dueDate === "" || submitting) {
      return;
    }
    setSubmitting(true);
    setError(null);
    setNotice(null);
    const payload = {
      level,
      name: name.trim(),
      size,
      due_date: dueDate,
      ...(team.trim() === "" ? {} : { team: team.trim() })
    };
    try {
      const result = await apiClient.createSubBlock(parentID, payload, keyFor(JSON.stringify(payload)));
      setName("");
      setSize(0);
      setTeam("");
      setDueDate("");
      reset();
      if ("approval" in result) {
        setNotice(`Submitted “${payload.name}” for approval.`);
      } else {
        onCreated();
      }
    } catch (caught) {
      if (caught instanceof ApiError) {
        reset();
      }
      setError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="form-row form-row--inline subblock-form">
      <label>
        New {levelLabel(level).toLowerCase()}
        <input
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Recognition"
          required
        />
      </label>
      <label>
        Size
        <input
          type="number"
          min={0}
          value={size}
          onChange={(event) => setSize(Math.max(0, Number(event.target.value)))}
        />
      </label>
      <label>
        Team
        <input type="text" value={team} onChange={(event) => setTeam(event.target.value)} placeholder="optional" />
      </label>
      <label>
        Due date
        <input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} required />
      </label>
      <button type="submit" className="btn btn--primary" disabled={submitting || name.trim() === "" || dueDate === ""}>
        {submitting ? "Adding…" : `Add ${levelLabel(level).toLowerCase()}`}
      </button>
      {error !== null && <p className="form-row__error">{error}</p>}
      {notice !== null && (
        <p className="form-row__success" role="status">
          {notice}
        </p>
      )}
    </form>
  );
}

// AssignPanel sets the block's owner and assignee via the tri-state assign
// endpoint: an unchanged field is omitted, a cleared picker sends null, a
// picked person sends their id. Per the ownership model, a mid block's owner
// is its team lead and an ic block's assignee is the IC who owns it.
function AssignPanel({ block, onSaved }: { block: BlockDetail; onSaved: () => void }) {
  const [owner, setOwner] = useState<UserPickerValue>(() => pickerFor(block.exec_owner));
  const [assignee, setAssignee] = useState<UserPickerValue>(() => pickerFor(block.assignee));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const members = useMemberRecipients(true);
  const { keyFor, reset } = useIdempotencyKey();

  const payload: AssignBlockInput = {};
  const ownerBefore = block.exec_owner?.id ?? "";
  const assigneeBefore = block.assignee?.id ?? "";
  if (owner.selectedID !== ownerBefore) {
    payload.exec_owner_user_id = owner.selectedID === "" ? null : owner.selectedID;
  }
  if (assignee.selectedID !== assigneeBefore) {
    payload.assignee_user_id = assignee.selectedID === "" ? null : assignee.selectedID;
  }
  const dirty = Object.keys(payload).length > 0;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!dirty || submitting) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.assignBlock(block.id, payload, keyFor(JSON.stringify(payload)));
      reset();
      onSaved();
    } catch (caught) {
      if (caught instanceof ApiError) {
        reset();
      }
      setError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="form-row form-row--inline">
      <UserPicker
        label="Owner"
        placeholder="Type a name or email…"
        value={owner}
        recipients={members.recipients}
        loadStatus={members.status}
        loadErrorMessage={members.errorMessage}
        loadingMessage="Loading members…"
        emptyMessage="No members in your tenant yet."
        noSelectionHint="Pick a person, or clear to unassign."
        onChange={setOwner}
      />
      <UserPicker
        label="Assignee"
        placeholder="Type a name or email…"
        value={assignee}
        recipients={members.recipients}
        loadStatus={members.status}
        loadErrorMessage={members.errorMessage}
        loadingMessage="Loading members…"
        emptyMessage="No members in your tenant yet."
        noSelectionHint="Pick a person, or clear to unassign."
        onChange={setAssignee}
      />
      <button type="submit" className="btn btn--primary" disabled={!dirty || submitting}>
        {submitting ? "Saving…" : "Save owners"}
      </button>
      {error !== null && <p className="form-row__error">{error}</p>}
    </form>
  );
}

function pickerFor(user: { id: string; name: string; email: string } | null): UserPickerValue {
  if (user === null) {
    return { query: "", selectedID: "" };
  }
  return { query: user.name.trim() === "" ? user.email : user.name, selectedID: user.id };
}

// ScorePanel edits a top block's Impact/Significance/override and shows the
// calculated size live alongside, so a Judicious sees both numbers before
// saving (req §5.4). The override input is the effective override: empty clears
// it, a number sets it; the server recomputes calc_size and size (C.7).
function ScorePanel({ block, onSaved }: { block: BlockDetail; onSaved: () => void }) {
  const [impact, setImpact] = useState(block.impact ?? 3);
  const [significance, setSignificance] = useState(block.significance ?? 3);
  const [override, setOverride] = useState(block.override_size !== null ? String(block.override_size) : "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { keyFor, reset } = useIdempotencyKey();

  const calc = calcBlockSize(impact, significance);
  const overrideNum = override.trim() === "" ? null : Number(override);
  const overrideValid = overrideNum === null || (Number.isFinite(overrideNum) && overrideNum >= 0);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting || !overrideValid) {
      return;
    }
    setSubmitting(true);
    setError(null);
    const payload: UpdateScoreInput = { impact, significance, override_size: overrideNum };
    try {
      await apiClient.updateBlockScore(block.id, payload, keyFor(JSON.stringify(payload)));
      reset();
      onSaved();
    } catch (caught) {
      if (caught instanceof ApiError) {
        reset();
      }
      setError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="form-row form-row--inline">
      <label>
        Impact
        <select value={impact} onChange={(event) => setImpact(Number(event.target.value))}>
          {scores.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </label>
      <label>
        Significance
        <select value={significance} onChange={(event) => setSignificance(Number(event.target.value))}>
          {scores.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </label>
      <label>
        Override
        <input
          type="number"
          min={0}
          value={override}
          onChange={(event) => setOverride(event.target.value)}
          placeholder={`calc ${calc}`}
        />
      </label>
      <span className="chip chip--impact">Calculated {calc}</span>
      <button type="submit" className="btn btn--primary" disabled={submitting || !overrideValid}>
        {submitting ? "Saving…" : "Save score"}
      </button>
      {error !== null && <p className="form-row__error">{error}</p>}
    </form>
  );
}

// CompletionPanel marks the block complete or, for Admin/Dynasty, reopens a
// complete one. The server decides whether a completion lands immediately or
// queues for sign-off (C.4); the reload shows which, via the status and the
// pending banner.
function CompletionPanel({
  block,
  canComplete,
  canReopen,
  onSaved
}: {
  block: BlockDetail;
  canComplete: boolean;
  canReopen: boolean;
  onSaved: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { keyFor, reset } = useIdempotencyKey();

  async function run(action: "complete" | "reopen") {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (action === "complete") {
        await apiClient.submitBlockCompletion(block.id, keyFor(`complete:${block.id}`));
      } else {
        await apiClient.updateBlockProgress(block.id, { status: "in-progress" }, keyFor(`reopen:${block.id}`));
      }
      reset();
      onSaved();
    } catch (caught) {
      // The server replays a stored answer for a reused key, so a refused
      // request needs a new key to be retried; a network error may never have
      // reached the server, so that retry keeps the key.
      if (caught instanceof ApiError) {
        reset();
      }
      setError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="form-row form-row--inline">
      {canComplete && (
        <button type="button" className="btn btn--primary" disabled={submitting} onClick={() => void run("complete")}>
          {submitting ? "Saving…" : "Mark complete"}
        </button>
      )}
      {canReopen && (
        <button type="button" className="btn btn--ghost" disabled={submitting} onClick={() => void run("reopen")}>
          {submitting ? "Saving…" : "Reopen"}
        </button>
      )}
      {error !== null && <p className="form-row__error">{error}</p>}
    </div>
  );
}

// ProgressPanel sets a sub-block's status/progress; on save the server rolls the
// change up every ancestor (C.2), so the parent's progress changes when you
// reopen its detail. Completion is absent here — it goes through
// CompletionPanel's submit-completion workflow.
function ProgressPanel({ block, onSaved }: { block: BlockDetail; onSaved: () => void }) {
  const [status, setStatus] = useState<"assigned" | "in-progress">(
    block.status === "assigned" ? "assigned" : "in-progress"
  );
  const [progress, setProgress] = useState(block.progress);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { keyFor, reset } = useIdempotencyKey();

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) {
      return;
    }
    setSubmitting(true);
    setError(null);
    const payload: UpdateProgressInput = { progress, status };
    try {
      await apiClient.updateBlockProgress(block.id, payload, keyFor(JSON.stringify(payload)));
      reset();
      onSaved();
    } catch (caught) {
      if (caught instanceof ApiError) {
        reset();
      }
      setError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="form-row form-row--inline">
      <label>
        Status
        <select value={status} onChange={(event) => setStatus(event.target.value as "assigned" | "in-progress")}>
          <option value="assigned">Assigned</option>
          <option value="in-progress">In Progress</option>
        </select>
      </label>
      <label>
        Progress %
        <input
          type="number"
          min={0}
          value={progress}
          onChange={(event) => setProgress(Math.max(0, Number(event.target.value)))}
        />
      </label>
      <button type="submit" className="btn btn--primary" disabled={submitting}>
        {submitting ? "Saving…" : "Save progress"}
      </button>
      {error !== null && <p className="form-row__error">{error}</p>}
    </form>
  );
}

// EditPanel edits a block's descriptive fields (name, acceptance criteria,
// team, due date; size on sub-blocks) plus its notes. Details and notes are
// separate endpoints — the panel sends only what changed, in that order.
function EditPanel({
  block,
  canEditDetails,
  canEditSize,
  canNotes,
  onSaved
}: {
  block: BlockDetail;
  canEditDetails: boolean;
  canEditSize: boolean;
  canNotes: boolean;
  onSaved: () => void;
}) {
  const [name, setName] = useState(block.name);
  const [acceptanceCriteria, setAcceptanceCriteria] = useState(block.acceptance_criteria ?? "");
  const [team, setTeam] = useState(block.team ?? "");
  const [dueDate, setDueDate] = useState(block.due_date ?? "");
  const [size, setSize] = useState(block.size);
  const [notes, setNotes] = useState(block.notes ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { keyFor, reset } = useIdempotencyKey();

  const details: UpdateBlockDetailsInput = {};
  if (name !== block.name) {
    details.name = name;
  }
  if (acceptanceCriteria !== (block.acceptance_criteria ?? "")) {
    details.acceptance_criteria = acceptanceCriteria;
  }
  if (team !== (block.team ?? "")) {
    details.team = team;
  }
  // A due date can't be cleared, so an emptied input is simply not sent.
  if (dueDate !== "" && dueDate !== (block.due_date ?? "")) {
    details.due_date = dueDate;
  }
  if (canEditSize && size !== block.size) {
    details.size = size;
  }
  const notesChanged = canNotes && notes !== (block.notes ?? "");
  const dirty = Object.keys(details).length > 0 || notesChanged;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting || !dirty) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (Object.keys(details).length > 0) {
        await apiClient.updateBlockDetails(block.id, details, keyFor(JSON.stringify(details)));
      }
      if (notesChanged) {
        await apiClient.updateBlockNotes(block.id, notes, keyFor(`notes:${notes}`));
      }
      reset();
      onSaved();
    } catch (caught) {
      if (caught instanceof ApiError) {
        reset();
      }
      setError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="form-row">
      {canEditDetails && (
        <>
          <label>
            Name
            <input type="text" value={name} maxLength={200} onChange={(event) => setName(event.target.value)} />
          </label>
          <label>
            Acceptance criteria
            <textarea
              value={acceptanceCriteria}
              rows={3}
              onChange={(event) => setAcceptanceCriteria(event.target.value)}
            />
          </label>
          <div className="form-row form-row--inline">
            <label>
              Team
              <input type="text" value={team} onChange={(event) => setTeam(event.target.value)} />
            </label>
            <label>
              Due date
              <input
                type="date"
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
                required={block.due_date !== null}
              />
            </label>
            {canEditSize && (
              <label>
                Size
                <input
                  type="number"
                  min={0}
                  value={size}
                  onChange={(event) => setSize(Math.max(0, Number(event.target.value)))}
                />
              </label>
            )}
          </div>
        </>
      )}
      {canNotes && (
        <label>
          Notes
          <textarea value={notes} rows={3} onChange={(event) => setNotes(event.target.value)} />
        </label>
      )}
      <button type="submit" className="btn btn--primary" disabled={submitting || !dirty || name.trim() === ""}>
        {submitting ? "Saving…" : "Save details"}
      </button>
      {error !== null && <p className="form-row__error">{error}</p>}
    </form>
  );
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Operation failed.";
}
